import React from 'react';
import { motion } from 'framer-motion';
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag, ShieldCheck, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCartStore } from '../../store/cartStore';

const Cart: React.FC = () => {
  const { items, removeItem, updateQuantity, total } = useCartStore();

  if (items.length === 0) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center px-6 noise-overlay">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="w-32 h-32 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-10 border border-white/10">
            <ShoppingBag className="w-12 h-12 text-gray-700" />
          </div>
          <h2 className="text-4xl font-serif font-bold mb-6">Your cosmic cart is empty</h2>
          <p className="text-gray-500 mb-12 max-w-md mx-auto font-light leading-relaxed">
            The stars are waiting for you. Explore our collection of jewelry and readings to begin your celestial journey.
          </p>
          <Link to="/shop" className="btn-primary inline-flex">
            Start Shopping
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-midnight py-24 noise-overlay">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <span className="text-gold text-[10px] font-bold uppercase tracking-[0.3em] mb-4 block">Your Selection</span>
          <h1 className="text-5xl font-serif font-bold">Cosmic Cart</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
          {/* Items List */}
          <div className="lg:col-span-2 space-y-8">
            {items.map((item, idx) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="flex flex-col sm:flex-row items-center gap-10 p-8 glass-card"
              >
                <div className="w-32 h-32 rounded-2xl overflow-hidden shrink-0 shadow-2xl">
                  <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                </div>
                <div className="flex-grow text-center sm:text-left">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-2xl font-serif font-bold text-white">{item.name}</h3>
                      <p className="text-gray-600 text-[10px] uppercase tracking-widest mt-1">{item.type}</p>
                    </div>
                    <p className="text-xl text-gold font-serif font-bold">₹{item.price.toLocaleString('en-IN')}</p>
                  </div>
                  
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-8">
                    <div className="flex items-center bg-white/5 border border-white/10 rounded-xl px-3 py-1">
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="p-2 text-gray-500 hover:text-gold transition-colors"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-10 text-center font-bold text-white text-sm">{item.quantity}</span>
                      <button 
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="p-2 text-gray-500 hover:text-gold transition-colors"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                    <button 
                      onClick={() => removeItem(item.id)}
                      className="flex items-center text-gray-600 hover:text-red-500 transition-colors text-[10px] font-bold uppercase tracking-widest"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Summary */}
          <div className="lg:col-span-1">
            <div className="p-10 glass-card sticky top-32">
              <h2 className="text-2xl font-serif font-bold mb-10">Order Summary</h2>
              <div className="space-y-6 mb-10">
                <div className="flex justify-between text-gray-500 text-sm">
                  <span>Subtotal</span>
                  <span className="text-white font-medium">₹{total().toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between text-gray-500 text-sm">
                  <span>Shipping</span>
                  <span className="text-green-500 font-bold uppercase tracking-widest text-[10px]">Complimentary</span>
                </div>
                <div className="flex justify-between text-gray-500 text-sm">
                  <span>Tax (GST)</span>
                  <span className="text-white font-medium">Included</span>
                </div>
                <div className="border-t border-white/5 pt-6 flex justify-between items-center">
                  <span className="text-white font-bold">Total</span>
                  <span className="text-3xl text-gold font-serif font-bold">₹{total().toLocaleString('en-IN')}</span>
                </div>
              </div>
              
              <Link
                to="/checkout"
                className="btn-primary w-full group"
              >
                Proceed to Checkout
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-2 transition-transform" />
              </Link>
              
              <div className="mt-10 space-y-4">
                <div className="flex items-center text-[10px] text-gray-600 uppercase tracking-widest">
                  <ShieldCheck className="w-4 h-4 mr-3 text-gold" />
                  Secure Checkout Guaranteed
                </div>
                <div className="flex items-center text-[10px] text-gray-600 uppercase tracking-widest">
                  <Lock className="w-4 h-4 mr-3 text-gold" />
                  256-bit SSL Encryption
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
